import React, { useState } from 'react';
import { AutomotiveRole, VideoEmailStatus, VideoStatus } from 'lib/const';
import styled from 'styled-components/macro';
import { startCase, toLower } from 'lodash';
import { VideoItemType } from './VideoItem';
import { VideoActivities } from './VideoActivities';
import { useGetVideoActivities } from 'lib/api/videoActivities/getVideoActivities';
import { StatusBadge, Tooltip } from 'react-covideo-common';
import { VideoActivity } from 'lib/context';
import { BadgeThemeKey } from 'react-covideo-common/dist/components/StatusBadge/types';

const Wrapper = styled.div`
  display: block;
`;

function getBadgeVariant(status: any): BadgeThemeKey {
  switch (status) {
    case VideoStatus.Approved:
    case VideoEmailStatus.Watched:
      return 'success';

    case VideoStatus.Denied:
    case VideoEmailStatus.NotSent:
    case VideoEmailStatus.Deleted:
      return 'error';

    case VideoEmailStatus.Sent:
      return 'primary';

    case VideoStatus.Pending:
    default:
      return 'neutral';
  }
}

type Props = {
  status?: VideoStatus;
  emailStatus?: VideoEmailStatus;
  videoItemType?: VideoItemType;
  automotiveRole?: string;
  videoId: string;
};
export const VideoItemStatus = ({
  status,
  emailStatus,
  videoItemType,
  automotiveRole,
  videoId,
}: Props) => {
  const { refetch } = useGetVideoActivities(videoId);
  const [delayHandler, setDelayHandler] = useState<any>(null);
  const [activities, setActivities] = useState<VideoActivity[]>([]);

  const fetchActivities = async () => {
    setDelayHandler(
      setTimeout(async () => {
        const response = await refetch().catch(err => err);
        if (response instanceof Error) {
          setActivities([{ activityText: 'Error loading activities.' }]);
          return;
        }
        if (!response?.data?.length) {
          setActivities([{ activityText: 'No activities to show.' }]);
          return;
        }
        setActivities(response.data);
      }, 500)
    );
  };

  let selectedStatus: string | undefined = status;
  if (
    videoItemType === VideoItemType.Video &&
    automotiveRole !== AutomotiveRole.TECHNICIAN
  ) {
    selectedStatus = emailStatus;
  }

  const prepareStatusForDisplay = (status: string = '') => {
    if (status === VideoStatus.Pending) {
      status = 'Awaiting Approval';
    }
    if (status === VideoEmailStatus.Watched) {
      status = 'Viewed';
    }
    return startCase(toLower(status));
  };

  return (
    <Wrapper
      onMouseEnter={() => fetchActivities()}
      onMouseLeave={() => {
        clearTimeout(delayHandler);
      }}
    >
      <Tooltip
        extendStyles={{ element: { cursor: 'pointer' } }}
        position='right'
        popup={<VideoActivities activities={activities} />}
      >
        <StatusBadge
          extendStyles={{
            container: { cursor: 'pointer', marginTop: '10px', width: '100%' },
          }}
          size='small'
          variant={getBadgeVariant(selectedStatus)}
          text={
            !!status
              ? prepareStatusForDisplay(selectedStatus)
              : 'Video Activity'
          }
        />
      </Tooltip>
    </Wrapper>
  );
};
